/* vuex 4 module handling persistance in localStorage and undo/redo.

undo/redo inspired by <https://github.com/factorial-io/undo-redo-vuex>
and <https://github.com/anthonygore/vuex-undo-redo> */

import { initNWKRecordAsJSON } from "@/data/NWKRecord";
import { initNWK } from "@/data/NWK";
import { MutationPayload, Store } from "vuex";
import { IStoreState } from ".";
import { initPseudonymState } from "./pseudonymPlugin";
import { ViewOptions, initDefaultViewOptions } from "@/data/ViewOptions";
import { initSessionState } from "./sessionModule";

const STORAGE_KEY = "easynwk";
const STORAGE_KEY_NWK = STORAGE_KEY + "_nwk";
const STORAGE_KEY_RECORD = STORAGE_KEY + "_record";
const STORAGE_KEY_VIEW = STORAGE_KEY + "_setting";
const UNREDO_MODULE = "unredo";

// ─── Harmony Bridge ──────────────────────────────────────────────────────────
const IS_IN_HARMONY = window.self !== window.top;

export interface IUnReDoState {
  undoCount: number;
  redoCount: number;
}

export function loadNWKStateFromStore(): string {
  if (IS_IN_HARMONY) return JSON.stringify(initNWK());
  const storedNWK = localStorage.getItem(STORAGE_KEY_NWK);
  if (storedNWK && storedNWK != "undefined") return storedNWK;
  return JSON.stringify(initNWK());
}

export function loadNWKRecordStateFromStore(): string {
  if (IS_IN_HARMONY) return initNWKRecordAsJSON();
  const storedNWKRecord = localStorage.getItem(STORAGE_KEY_RECORD);
  if (storedNWKRecord && storedNWKRecord != "undefined") return storedNWKRecord;
  return initNWKRecordAsJSON();
}

export function loadViewSettingsFromStore(): ViewOptions {
  const storedViewSettings = localStorage.getItem(STORAGE_KEY_VIEW);
  if (storedViewSettings && storedViewSettings != "undefined") {
    return { ...initDefaultViewOptions(), ...JSON.parse(storedViewSettings) };
  }
  return initDefaultViewOptions();
}

// ─── Dark Mode Helper ─────────────────────────────────────────────────────────
// Setzt/entfernt die Klasse "dark" auf <html> — main.scss reagiert darauf
function applyDarkMode(dark: boolean) {
  if (dark) {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
}

export const localStoragePlugin = (store: Store<IStoreState>): void => {
  const history = {
    initialNWKState: loadNWKStateFromStore(),
    initialNWKRecordState: loadNWKRecordStateFromStore(),
    initialViewSettings: loadViewSettingsFromStore(),
    done: [] as Array<MutationPayload>,
    undone: [] as Array<MutationPayload>,
    replaying: false,
  };

  store.registerModule(UNREDO_MODULE, {
    namespaced: true,
    state: {
      undoCount: 0,
      redoCount: 0,
    },
    mutations: {
      undo(state: IUnReDoState) {
        const last = history.done.pop();
        if (last) {
          state.undoCount--;
          history.undone.push(last);
          state.redoCount++;
        }
        history.replaying = true;
        store.state.pseudonym = initPseudonymState();
        store.commit("loadJSON", history.initialNWKRecordState);
        store.commit("restoreNWKFromJSON", history.initialNWKState);
        store.commit("view/update", history.initialViewSettings);
        store.state.session = initSessionState();
        for (const c of history.done) {
          store.commit(c.type, c.payload);
        }
        history.replaying = false;
      },

      redo(state: IUnReDoState) {
        if (state.redoCount == 0) history.undone = [];
        const last = history.undone.pop();
        if (last) {
          state.redoCount--;
          history.done.push(last);
          state.undoCount++;
          history.replaying = true;
          store.commit(last.type, last.payload);
          history.replaying = false;
        }
      },

      usermutation(state: IUnReDoState) {
        state.undoCount = history.done.length;
        state.redoCount = 0;
      },
    },
  });

  // ─── Harmony postMessage Listener ──────────────────────────────────────────
  if (IS_IN_HARMONY) {
    window.addEventListener("message", (event) => {
      // Im Prod: if (event.origin !== "https://app.fehyra.de") return

      if (event.data?.type === "HARMONY_LOAD") {
        try {
          // ── Dark Mode anwenden ─────────────────────────────────────────
          applyDarkMode(event.data.darkMode === true);

          // ── NWK-Daten laden ────────────────────────────────────────────
          const recordJSON = JSON.stringify(event.data.record);
          store.commit("loadJSON", recordJSON);
          history.initialNWKRecordState = recordJSON;
          history.initialNWKState = JSON.stringify(
            event.data.record?.versions?.[0]?.nwk ?? initNWK()
          );
        } catch (e) {
          console.error("[Harmony Bridge] Fehler beim Laden der NWK:", e);
        }
      }

      // ── Dark Mode kann auch nachträglich geändert werden ───────────────
      // z.B. wenn User den Toggle in Harmony klickt während Modal offen ist
      if (event.data?.type === "HARMONY_DARKMODE") {
        applyDarkMode(event.data.darkMode === true);
      }
    });

    // Harmony signalisieren dass easyNWK bereit ist
    window.parent.postMessage({ type: "EASYNWK_READY" }, "*");
  }

  // ─── Undo History tracken ─────────────────────────────────────────────────
  store.subscribe((mutation) => {
    if (!mutation.type.startsWith(UNREDO_MODULE)) {
      if (!history.replaying) {
        history.done.push(mutation);
        store.commit(UNREDO_MODULE + "/usermutation");
      }
    }
  });

  // ─── Persistierung ────────────────────────────────────────────────────────
  store.subscribe((mutation, stateAfter: IStoreState) => {
    if (
      !(
        history.replaying ||
        mutation.type === UNREDO_MODULE + "/usermutation" ||
        mutation.type.startsWith("view/")
      )
    ) {
      if (IS_IN_HARMONY) {
        // JSON-Klon entfernt Vue-Proxies → behebt "object can not be cloned"
        try {
          window.parent.postMessage(
            {
              type: "HARMONY_SAVE",
              record: JSON.parse(JSON.stringify(stateAfter.record)),
              nwk: JSON.parse(JSON.stringify(stateAfter.nwk)),
            },
            "*"
          );
        } catch (e) {
          console.error("[Harmony Bridge] postMessage fehlgeschlagen:", e);
        }
      } else {
        localStorage.setItem(STORAGE_KEY_NWK, JSON.stringify(stateAfter.nwk));
        localStorage.setItem(
          STORAGE_KEY_RECORD,
          JSON.stringify(stateAfter.record)
        );
      }
    }

    if (!history.replaying && mutation.type.startsWith("view/")) {
      localStorage.setItem(STORAGE_KEY_VIEW, JSON.stringify(stateAfter.view));
    }
  });
};